import React from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { TrendingUp, DollarSign } from 'react-feather';

interface ExpenseTrendProps {
    data: {
        months: string[];
        data: number[];
    };
}

export default function ExpenseTrendChart({ data }: ExpenseTrendProps) {
    if (!data) return null;
    const totalExpense = data.data.reduce((sum, val) => sum + Number(val), 0);
    const avgExpense = data.data.length ? totalExpense / data.data.length : 0;
    const currentMonth = Number(data.data[data.data.length - 1]) || 0;
    const previousMonth = Number(data.data[data.data.length - 2]) || 0;

    // Calculate percent change with proper edge case handling
    let percentChange = 0;
    if (previousMonth === 0) {
        // If previous month was 0, show 100% increase if current > 0, else 0%
        percentChange = currentMonth > 0 ? 100 : 0;
    } else {
        percentChange = ((currentMonth - previousMonth) / previousMonth) * 100;
    }

    const options: ApexOptions = {
        chart: {
            type: 'area',
            height: 230,
            toolbar: {
                show: true,
                tools: {
                    download: true,
                    selection: false,
                    zoom: false,
                    zoomin: false,
                    zoomout: false,
                    pan: false,
                    reset: false,
                },
            },
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800,
            },
            dropShadow: {
                enabled: true,
                top: 0,
                left: 0,
                blur: 3,
                opacity: 0.1,
            },
        },
        dataLabels: {
            enabled: false,
        },
        stroke: {
            curve: 'smooth',
            width: 3,
            colors: ['#667eea'],
        },
        colors: ['#667eea'],
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.7,
                opacityTo: 0.1,
                stops: [0, 90, 100],
                colorStops: [
                    {
                        offset: 0,
                        color: '#667eea',
                        opacity: 0.7,
                    },
                    {
                        offset: 100,
                        color: '#764ba2',
                        opacity: 0.1,
                    },
                ],
            },
        },
        xaxis: {
            categories: data.months,
            labels: {
                style: {
                    fontSize: '12px',
                    fontWeight: 600,
                    colors: '#718096',
                },
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
        },
        yaxis: {
            labels: {
                formatter: (value) => {
                    return new Intl.NumberFormat('en-US', {
                        style: 'decimal',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    }).format(value);
                },
                style: {
                    fontSize: '12px',
                    fontWeight: 600,
                    colors: '#718096',
                },
            },
        },
        tooltip: {
            enabled: true,
            theme: 'light',
            style: {
                fontSize: '14px',
            },
            y: {
                formatter: (value) => {
                    return new Intl.NumberFormat('en-US', {
                        style: 'decimal',
                        minimumFractionDigits: 2,
                    }).format(value);
                },
            },
            marker: {
                show: true,
            },
        },
        grid: {
            borderColor: '#e2e8f0',
            strokeDashArray: 4,
            xaxis: {
                lines: {
                    show: false,
                },
            },
            yaxis: {
                lines: {
                    show: true,
                },
            },
            padding: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 10,
            },
        },
        markers: {
            size: 0,
            hover: {
                size: 6,
                sizeOffset: 3,
            },
        },
    };

    const series = [
        {
            name: 'Total Expenses',
            data: data.data,
        },
    ];

    return (
        <Card className="chart-card" style={{ height: '100%' }}>
            <CardHeader className="pb-0">
                <div className="d-flex align-items-start justify-content-between">
                    <div className="flex-grow-1">
                        <h5 className="mb-1">Expense Trend</h5>
                        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                            LPO Expenses (Parts & Labour)
                        </p>
                    </div>
                    <div
                        className="stat-card-icon"
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            boxShadow: '0 4px 14px 0 rgba(102, 126, 234, 0.4)',
                            width: '48px',
                            height: '48px',
                        }}
                    >
                        <TrendingUp size={22} color="white" strokeWidth={2.5} />
                    </div>
                </div>

                {/* Summary Stats */}
                <div className="row mt-3 g-3">
                    <div className="col-4">
                        <div className="p-2 rounded" style={{ background: '#f7fafc' }}>
                            <small className="text-muted d-block mb-1" style={{ fontSize: '0.75rem' }}>Total (6M)</small>
                            <strong style={{ fontSize: '0.9375rem', color: '#2d3748' }}>
                                {(totalExpense / 1000).toFixed(1)}K
                            </strong>
                        </div>
                    </div>
                    <div className="col-4">
                        <div className="p-2 rounded" style={{ background: '#f7fafc' }}>
                            <small className="text-muted d-block mb-1" style={{ fontSize: '0.75rem' }}>Average</small>
                            <strong style={{ fontSize: '0.9375rem', color: '#2d3748' }}>
                                {(avgExpense / 1000).toFixed(1)}K
                            </strong>
                        </div>
                    </div>
                    <div className="col-4">
                        <div className="p-2 rounded" style={{ background: percentChange >= 0 ? '#fee' : '#f0fdf4' }}>
                            <small className="text-muted d-block mb-1" style={{ fontSize: '0.75rem' }}>Change</small>
                            <strong style={{
                                fontSize: '0.9375rem',
                                color: percentChange >= 0 ? '#ef4444' : '#10b981'
                            }}>
                                {percentChange >= 0 ? '+' : ''}{percentChange.toFixed(1)}%
                            </strong>
                        </div>
                    </div>
                </div>
            </CardHeader>
            <CardBody>
                <ReactApexChart options={options} series={series} type="area" height={230} />
            </CardBody>
        </Card>
    );
}

